import { DateTime } from 'luxon';
import { AutocompleteOption } from 'redesign';
import { getBranchOffices, getDepartments, getJobTypes } from './utils';
import { ShiftPlanningFilters } from '../../table/hooks/use-shift-planning.types';
import {
  ShiftPlanningFiltersData,
  ShiftPlanningFiltersFormType,
} from '../shift-planning-filters.types';

const getShiftPlanningFiltersDefaultValues = (
  appliedFilters: ShiftPlanningFilters,
  filterData: ShiftPlanningFiltersData
): ShiftPlanningFiltersFormType => {
  const fromDate = appliedFilters.fromDate
    ? appliedFilters.fromDate.toFormat('yyyy-MM-dd')
    : DateTime.now().toFormat('yyyy-MM-dd');
  const result: ShiftPlanningFiltersFormType = {
    fromDate,
    company: null,
    branchOffices: null,
    departments: null,
    jobType: null,
    isFreelance: appliedFilters.isFreelance ?? false,
    isTempWork: appliedFilters.isTempWork ?? false,
    isEmployee: appliedFilters.isEmployee ?? false,
    areFreelanceApplications: appliedFilters.areFreelanceApplications ?? false,
    areTempworkApplications: appliedFilters.areTempworkApplications ?? false,
    areNoApplications: appliedFilters.areNoApplications ?? false,
    filledStatusCheckboxValue: !!appliedFilters.filledStatus,
    jobTypeCheckboxValue: !!appliedFilters.jobType?.length,
    filledStatus: appliedFilters.filledStatus ? appliedFilters.filledStatus : 'inProgress',
    businessUnitCheckboxValue: !!appliedFilters.company,
    fromDateCheckboxValue: !!appliedFilters.fromDate,
    employmentTypeCheckboxValue: !!appliedFilters.isFreelance || !!appliedFilters.isTempWork,
    applicationTypesCheckboxValue:
      !!appliedFilters.areFreelanceApplications ||
      !!appliedFilters.areTempworkApplications ||
      !!appliedFilters.areNoApplications,
  };

  if (appliedFilters.jobType?.length) {
    const jobTypes = getJobTypes(appliedFilters.jobType, filterData.jobTypes);
    if (jobTypes?.length) {
      result.jobType = jobTypes.map((jt) => ({ id: jt.value, name: jt.label }));
    }
  }

  const allBranchOffices = filterData.companies.flatMap((c) => c.branchOffices ?? []);
  if (appliedFilters.branchOfficeIds?.length) {
    result.branchOffices = getBranchOffices(appliedFilters.branchOfficeIds, allBranchOffices);
  }

  if (appliedFilters.departmentIds?.length) {
    result.departments = getDepartments(appliedFilters.departmentIds, allBranchOffices);
  }

  if (!appliedFilters.company) {
    return result;
  }
  if (!filterData?.companies) {
    return result;
  }
  const companyFilter = filterData.companies.find((c) => c.companyId === appliedFilters.company);

  if (!companyFilter) {
    return result;
  }

  const company: AutocompleteOption = {
    value: companyFilter.companyId,
    label: companyFilter.companyName,
  };
  result.company = company;

  if (!appliedFilters.branchOfficeIds?.length) {
    return result;
  }

  const branchOfficeFilter = companyFilter.branchOffices.find((bo) =>
    appliedFilters.branchOfficeIds?.includes(bo.branchOfficeId)
  );

  if (!branchOfficeFilter) {
    return result;
  }

  if (!appliedFilters.departmentIds?.length) {
    return result;
  }

  const departmentFilter = branchOfficeFilter.departments.find((dept) =>
    appliedFilters.departmentIds?.includes(dept.departmentId)
  );

  if (!departmentFilter) {
    return result;
  }

  return result;
};
export { getShiftPlanningFiltersDefaultValues };
